import {
  ActionValidation,
  BooleanValidation,
  StringValidation,
} from '../../../utils/component-validations';
import { buildWaiter } from '@ember/test-waiters';
import { modifier } from 'ember-modifier';
import { or } from 'macro-decorators';
import { service } from '@ember/service';
import { testAwareWaitForLater, waitForRender } from '../../../utils/waiters';
import { tracked } from '@glimmer/tracking';
import Super from './base.gjs';
import dialogPolyfill from 'dialog-polyfill';
import styles from './modal.module.scss';

/**
 * Base class for SPDS modal variants
 *
 * Arguments
 * @argument {boolean} closeOnClickOutside controls whether the modal should automatically close when user clicks outside of its DOM tree
 * @argument {function} onBeforeClose gives a hook where close behavior can be interrupted by custom behavior
 * @argument {function} onClose gives a hook to perform custom behavior when the modal closes
 * @argument {function} onOpen gives a hook to perform custom behavior when the modal opens
 * @argument {boolean} renderedWithPromise indicates whether the modal is being rendered with a promise, which requires special handling for waiters; only used by global modal service
 * @argument {string} variantName allows for wrapping components to provide a custom component name for argument validation messages
 */
export default class SpdsCoreModal extends Super {
  baseArguments = [
    new ActionValidation({ optional: ['onBeforeClose', 'onOpen'], required: ['onClose'] }),
    new BooleanValidation({ optional: ['closeOnClickOutside', 'renderedWithPromise'] }),
    new StringValidation({ optional: ['variantName'] }),
  ];
  baseStyles = styles;
  classesToMapToAttrs = ['isVisible'];
  closeOnClickOutsideDefault = false;
  defaultComponentName = 'Spds::BaseModal';
  dialog;
  token;

  @service bodyScrollManager;
  @service fastboot;

  @tracked isVisible = false;

  @or('args.closeOnClickOutside', 'closeOnClickOutsideDefault') closeOnClickOutside;
  @or('args.variantName', 'defaultComponentName') componentName;

  handleClickOnDialog = async evt => {
    if (evt.target === this.dialog) {
      await this.beforeClose();
    }
  };

  /**
   * arguments for validation
   * allows modal variants to provide their own set of arguments aside from the basic modal arguments
   * @return {array} array of validation objects
   */
  get arguments() {
    let { baseArguments, variantArguments = [] } = this;

    return [...baseArguments, ...variantArguments];
  }

  /**
   * @constructor set this.waiter to a custom waiter for this modal instance
   * using this.constructor.name and the generated uniqueIdentifier from the core/base class
   */
  constructor() {
    super(...arguments);

    this.waiter = buildWaiter(
      `@ember-simplepractice/${this.constructor.name}/${this.uniqueIdentifier}`
    );

    if (!this.args.renderedWithPromise) {
      this.token = this.waiter.beginAsync();
    }
  }

  /**
   * modifier that handles setting up modal behavior
   * if the browser does not support the modern HTML dialog, uses a polyfill to provide equivalent behavior
   * sets up event listeners to handle close via Escape key, clicking outside the modal, and
   * common behavior that should trigger on close
   * also fires onOpen when provided
   */
  onInsert = modifier(element => {
    if (this.dialog?.open) {
      return;
    }

    if (!this.fastboot?.isFastBoot) {
      if (!window.HTMLDialogElement) {
        dialogPolyfill.registerDialog(element);
        this.variantClass = 'spds-dialog-polyfill-modal';
      }
    }

    this.dialog = element;

    this.#show();

    /**
     * cancelListener catches Escape key press and determines whether the modal should actually close
     */
    let cancelListener = async evt => {
      evt.preventDefault();

      if (!(await this.#beforeClose(evt))) {
        return;
      }

      await this.close();
    };

    /**
     * closeListener cleans up the event listeners for `cancel` and `close` events,
     * then ends the test waiter and triggers the private onClose function
     */
    let closeListener = async () => {
      this.#cleanupListeners(cancelListener, closeListener);
      this.waiter.endAsync(this.token);

      this.args.onClose?.();
    };

    this.dialog.addEventListener('cancel', cancelListener);
    this.dialog.addEventListener('close', closeListener);

    if (this.modal && this.closeOnClickOutside) {
      this.dialog.addEventListener('click', this.handleClickOnDialog);
    }

    this.args.onOpen?.();
  });

  /**
   * function that checks return from onBeforeClose handler to see if modal close should continue
   * defaults to allowing close when no handler is provided
   */
  async #beforeClose(evt) {
    if (this.args.onBeforeClose) {
      return Promise.resolve(this.args.onBeforeClose(evt));
    }

    return true;
  }

  /**
   * private function for cleaning up event handlers on teardown
   */
  #cleanupListeners(cancel, close) {
    this.dialog.removeEventListener('cancel', cancel);
    this.dialog.removeEventListener('close', close);
  }

  /**
   * private function for setting up modal-specific functionality
   * instantiates dialog element with backdrop through `showModal` and
   * prevents body scrolling through the `bodyScrollManager.lockScroll` function
   */
  #modalSetup() {
    this.dialog.showModal();

    if (this.fastboot?.isFastBoot) return;

    this.bodyScrollManager.lockScroll();
  }

  /**
   * private function for displaying modal
   * waits 1ms for modal to be added to the DOM to allow animations to play,
   * then performs show behavior dependent on whether the modal backdrop should
   * be instantiated
   */
  async #show() {
    this.isVisible = true;
    // allow to render before opening so that transition will be applied
    // Ensure dialog element is in DOM before calling showModal()
    await waitForRender();

    if (this.modal) {
      this.#modalSetup();
    } else {
      this.dialog.show();
    }

    if (this.token) {
      this.waiter.endAsync(this.token);
    }
  }

  /**
   * willDestroy lifecycle cleans up remaining modal logic,
   * allowing body scrolling to resume and removing the click handler
   * for closing on click outside if it was added
   */
  willDestroy() {
    super.willDestroy(...arguments);

    if (!this.fastboot?.isFastBoot) {
      this.bodyScrollManager.unlockScroll();
    }

    if (this.modal && this.closeOnClickOutside) {
      this.dialog.removeEventListener('click', this.handleClickOnDialog);
    }
  }

  /**
   * public onBeforeClose waits for result from private beforeClose function,
   * then continues closing the modal if truthy response is received
   */
  beforeClose = async evt => {
    if (await this.#beforeClose(evt)) {
      this.close();
    }
  };

  /**
   * public close function begins test waiter for modal close, then uses built-in
   * browser behavior for closing the modal, triggering the `close` event
   */
  close = async () => {
    this.token = this.waiter.beginAsync();
    this.isVisible = false;
    await testAwareWaitForLater(300);
    this.dialog.close();
  };
}
